import { useEffect, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";
import { useAuth } from "@/context/AuthContext";
import { ADMIN_MODULES, MAIN_MODULES } from "@/lib/modules";
import { DEPARTMENTS } from "@/lib/workspace";
import { Logo } from "@/components/common/Logo";
import { NAV } from "@/design/tokens";

const NavItem = ({ item, label, onNavigate }) => {
  const Icon = item.icon;
  return (
    <NavLink to={item.path} onClick={onNavigate} data-testid={`nav-item-${item.id}`}
      className={({ isActive }) => cn(NAV.item, isActive ? NAV.active : NAV.idle)}>
      {({ isActive }) => (
        <>
          <Icon className={cn("h-[18px] w-[18px] shrink-0", isActive ? NAV.iconActive : NAV.iconIdle)} strokeWidth={1.75} />
          <span className="truncate">{label || item.label}</span>
        </>
      )}
    </NavLink>
  );
};

const STORE = "onsuite:nav-closed";
const readClosed = () => { try { return JSON.parse(localStorage.getItem(STORE)) || []; } catch { return []; } };

const isActive = (pathname, path) => pathname === path || pathname.startsWith(`${path}/`);

const Department = ({ dept, label, items, closed, toggle, onNavigate, term }) => {
  const { pathname } = useLocation();
  const hasActive = items.some((m) => isActive(pathname, m.path));
  useEffect(() => {
    if (hasActive && closed) { toggle(); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);
  const open = !closed;
  return (
    <div data-testid={`sidebar-dept-${dept}`}>
      <button type="button" onClick={toggle} data-testid={`sidebar-dept-toggle-${dept}`}
        className="flex w-full items-center justify-between rounded-control px-3 py-1.5 text-[11px] font-semibold uppercase tracking-[0.08em] text-slate-400 transition-colors hover:text-slate-600">
        <span className="truncate">{label}</span>
        <ChevronDown className={cn("h-3.5 w-3.5 transition-transform duration-200", !open && "-rotate-90")} />
      </button>
      {open && <div className="mt-0.5 space-y-0.5">{items.map((m) => <NavItem key={m.id} item={m} label={term(m.id, m.label)} onNavigate={onNavigate} />)}</div>}
    </div>
  );
};

export const Sidebar = ({ onNavigate }) => {
  const { can, me, term } = useAuth();
  const [closed, setClosed] = useState(readClosed);
  const toggle = (id) => setClosed((prev) => {
    const next = prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id];
    localStorage.setItem(STORE, JSON.stringify(next));
    return next;
  });
  const hidden = me?.workspace?.hidden_departments || [];
  const visible = (m) => (!m.perm ? !(m.id === "password-manager" && me?.is_demo_user) : can(m.perm));
  const byId = Object.fromEntries(MAIN_MODULES.filter(visible).map((m) => [m.id, m]));
  const depts = DEPARTMENTS.filter((d) => !hidden.includes(d.id))
    .map((d) => ({ ...d, items: d.modules.map((id) => byId[id]).filter(Boolean) }))
    .filter((d) => d.items.length);
  const admin = ADMIN_MODULES.filter((m) => (m.perm === "__platform" ? me?.platform_admin : can(m.perm)) && !me?.is_demo_user);
  return (
    <nav className="flex h-full w-full flex-col" data-testid="sidebar">
      <div className="flex h-16 shrink-0 items-center border-b border-line px-5">
        <Logo />
      </div>
      <div className="flex-1 space-y-4 overflow-y-auto px-3 py-5">
        {byId.dashboard && <NavItem item={byId.dashboard} onNavigate={onNavigate} />}
        {depts.map((d) => (
          <Department key={d.id} dept={d.id} label={term(`dept:${d.id}`, d.label)} items={d.items} closed={closed.includes(d.id)}
            toggle={() => toggle(d.id)} onNavigate={onNavigate} term={term} />
        ))}
        {admin.length > 0 && (
          <div data-testid="sidebar-admin-section">
            <Department dept="admin" label="Impostazioni" items={admin} closed={closed.includes("admin")}
              toggle={() => toggle("admin")} onNavigate={onNavigate} term={(_, l) => l} />
          </div>
        )}
      </div>
    </nav>
  );
};
